import { useRouter } from 'next/router';
import Image from 'next/image';
import clsx from 'clsx';
import AppRoutes from '@/const/routes';
import { Button, Icon } from 'components/common';
import { Typography } from '../../common/typography/typography';
import { ContainerBase } from '../containers/containerBase';
import { customEvent } from '@/lib';
import Icons from '@/const/icons';
import { useRef, useState } from 'react';

interface Props {
	className?: string;
}

export const Banner: React.FC<Props> = ({ className }) => {
	const router = useRouter();
	const videoRef = useRef<HTMLVideoElement>(null);

	const [isPlaying, setIsPlaying] = useState(false);

	const handlePlay = () => {
		if (videoRef.current) {
			videoRef.current.play();
			setIsPlaying(true);
		}
	};

	const handleEnded = () => {
		setIsPlaying(false);
	};

	return (
		<div className=" pt-[64.6px]">
			<ContainerBase
				className={clsx('w-full lg:!pr-0 4xl:!px-[96px] pb-[100px]', className)}
			>
				<div className={clsx('flex w-full items-center justify-between ')}>
					<div className="w-full xl:w-[60%] 2xl:w-[70%]">
						<div className="space-y-5 md:space-y-4">
							<Typography
								type="headline-2"
								className="text-center lg:text-left !text-[40px] lg:!text-6xl"
							>
								<span className="text-primary">Claim</span> What&apos;s Yours
							</Typography>
							<Typography
								type="custom-h3"
								className="!text-2xl xl:!text-[36px] font-bold text-center xl:text-left px-3 md:px-0 !leading-[130%]"
							>
								Sign Up, Track, Get Paid <br className="hidden lg:block" /> All
								in One Place
							</Typography>

							<Typography
								type="regular-18"
								className="!leading-[150%] !mt-10 text-center lg:text-left"
							>
								Modern technology and innovative lawyers working together to{' '}
								<br className="hidden lg:block" /> make claiming seamless.
							</Typography>
						</div>
						<div className="flex flex-col sm:flex-row items-center justify-center xl:justify-start gap-3 mt-10">
							<Button
								size="large"
								decoration="primary"
								className="lg:!w-[11.625rem] !px-0 order-2 md:order-none"
								onClick={() => {
									customEvent({
										action: 'explore_all_cases_click',
										category: 'landing',
										label: 'Explore All Cases',
										value: 'Explore All Cases',
										callback: () => router.push(AppRoutes.LANDING_CASES),
									});
								}}
							>
								Explore Cases
							</Button>
						</div>
					</div>
					<div className="hidden xl:block relative max-h-[538px] 4xl:max-h-[800px]">
						<Image
							src={Icons.hero}
							alt="banner"
							width={728}
							height={538}
							className="4xl:hidden"
						/>
						<Image
							src={Icons.heroLarge}
							alt="banner"
							width={1258}
							height={538}
							className="hidden 4xl:block"
						/>
					</div>
				</div>
			</ContainerBase>
			<ContainerBase className={clsx('w-full  pb-[100px]', className)}>
				<div className="pt-[96px] lg:pt-[150px]">
					<div className="bg-white border border-[#0D061614] stroke-[8%] shadow-shadow2 h-[122px] lg:h-[568.51px] w-full rounded-[32px] lg:rounded-[64px] relative">
						<video
							ref={videoRef}
							playsInline
							src="/videos/home.mp4"
							controls={isPlaying}
							onEnded={handleEnded}
							poster={Icons.dashboardImage}
							className="absolute left-1/2 -translate-x-1/2 -top-[70px] lg:-top-[90px] rounded-3xl w-[302px] h-[168px] lg:w-[1080px] lg:h-[597.83px] backdrop-blur-sm"
						/>
						{!isPlaying && (
							<button
								onClick={handlePlay}
								className={clsx(
									'absolute left-1/2 -translate-x-1/2 z-20 -top-[15px] lg:top-1/3 bg-primary transition-all',
									'rounded-full w-[54px] h-[54px] lg:w-[74px] lg:h-[74px] flex items-center justify-center'
								)}
							>
								<Icon src={Icons.play} className="!size-7 lg:size-10" />
							</button>
						)}
					</div>
				</div>
			</ContainerBase>
		</div>
	);
};
